import React, { useState, useEffect, useRef, useContext } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import withAppContext from "tbk-components/src/hooks/withAppContext";

export interface IStatBarStatProps extends IBaseElementProps {
	dynamicStat?: any;
	/**
	 * Use this field to override the dynamic stat value
	 */
	count?: number;
	/**
	 * This option allows you to hide the stat number entirely, so you can use the text / prefix / suffix functionality by itself
	 */
	hideStat?: boolean;
	statText?: string;
	showCommas?: boolean;
	prefix?: string;
	suffix?: string;
	countUp?: boolean;
	countUpDuration?: number;
}

/**
 * Stats
 * @block
 * @icon editor-ol-rtl
 */
const StatBarStat: React.FC<IStatBarStatProps> = withAppContext(
({
	context,
	dynamicStat,
	count,
	hideStat = false,
	showCommas = true,
	prefix,
	suffix,
	countUp = true,
	countUpDuration = 2000,
	statText,
	className,
	classNames = [],
}) => {
	const { Api } = useContext(context);
	const ref = useRef(null);
	const [countDisplay, setCountDisplay] = useState(0);
	//If the HTML is french language, use a space, else use a comma
	const langSeparator =
		document.querySelectorAll('[lang="fr-FR"], [lang="fr-ca"]')?.length > 0
			? ' '
			: ',';

	const numberWithCommas = function (x) {
		return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, langSeparator);
	};

	useEffect( () => {
		if ( count ) {
			setCountDisplay(count);
			return;
		}
		if ( dynamicStat ) {
			//translate the dynamicStat value to a number
			Api.getDynamicStatValue({dynamicStat: dynamicStat})
				.then((response) => {
				setCountDisplay(response);
			});
			return;
		}
	}, [ dynamicStat, count ]);

	useEffect(() => {
		if (!countUp || !ref.current) {
			return;
		}

		const counterEl = ref.current! as HTMLSpanElement;
		const frameDuration = 1000 / 60;
		const countUpDurationValidationed = Math.max(
			100,
			countUpDuration || 2000,
		); // Stops from breaking when number less that 100
		const totalFrames = Math.round(
			countUpDurationValidationed / frameDuration,
		);
		const easeOutQuad = (t: number) => t * (2 - t);
		const countTo = parseInt(counterEl.innerHTML, 10);
		let wasAnimated = false;

		const animateCountUp = () => {
			let frame = 0;
			const counter = setInterval(() => {
				frame++;

				const progress = easeOutQuad(frame / totalFrames);
				const currentCount = Math.round(countTo * progress);

				if (parseInt(counterEl.innerHTML, 10) !== currentCount) {
					if (showCommas) {
						counterEl.innerHTML = `${numberWithCommas(
							currentCount,
						)}`;
					} else {
						counterEl.innerHTML = `${currentCount}`;
					}
				}

				if (frame === totalFrames) {
					clearInterval(counter);
				}
			}, frameDuration);
		};

		const isElementInViewport = (el: HTMLSpanElement) => {
			const rect = el.getBoundingClientRect();

			return (
				rect.top >= 0 &&
				rect.left >= 0 &&
				rect.bottom <=
					(window.innerHeight ||
						document.documentElement.clientHeight) &&
				rect.right <=
					(window.innerWidth || document.documentElement.clientWidth)
			);
		};

		const handleScroll = () => {
			if (wasAnimated === false && isElementInViewport(counterEl)) {
				animateCountUp();
				wasAnimated = true;
			}
		};

		window.addEventListener('scroll', handleScroll);

		counterEl.innerHTML = '0';
		handleScroll();

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	/**
	 * countDisplay is not updated by the time the component is mounted
	 * so we need to add it to the dependencies array to make sure ref.current is defined
	 *  */
	}, [ countDisplay ]);

	return (
		<div className={className || cn('stat', ...classNames)}>
			{(countDisplay || prefix) && (
				<span className="stat-count lh-1">
					{prefix && (
						<span className="stat-count__prefix fs-4 fw-bolder ls-0">
							{prefix}
						</span>
					)}
					{ ! hideStat && countDisplay && (
						<span
							className="stat-count__number fs-4 fw-bolder ls-0 lh-1"
							ref={ref}
						>
							{showCommas && !countUp
								? numberWithCommas(countDisplay)
								: countDisplay}
						</span>
					)}
					{suffix && (
						<span className="stat-count__suffix fs-4 fw-bolder ls-0">
							{suffix}
						</span>
					)}
				</span>
			)}
			<span className={'stat-text fs-4 fw-light ls-0 lh-1'}>
				{statText}
			</span>
		</div>
	);
});

export default StatBarStat;
